import { forwardRef, useEffect, useRef, useState, type SelectHTMLAttributes } from 'react';
import { cn } from '../lib/cn';

interface Option {
  value: string;
  label: string;
}

type Props = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string;
  options: Option[];
  error?: string;
};

/**
 * Reads the live `color` value the select element resolves to (which respects
 * the active theme — Telegram light/dark + Pages default) and uses it to
 * paint the chevron. Avoids the prior hard-coded navy hex that was invisible
 * in dark themes.
 */
function useResolvedColor(): { ref: (el: HTMLElement | null) => void; color: string } {
  const elRef = useRef<HTMLElement | null>(null);
  const [color, setColor] = useState<string>('currentColor');

  useEffect(() => {
    if (!elRef.current) return;
    const c = window.getComputedStyle(elRef.current).color;
    if (c) setColor(c);
  }, []);

  return {
    ref: (el) => {
      elRef.current = el;
    },
    color,
  };
}

export const Select = forwardRef<HTMLSelectElement, Props>(function Select(
  { label, options, error, className, id, ...props },
  ref,
) {
  const { ref: probeRef, color } = useResolvedColor();
  // Encode the resolved CSS color into the inline SVG. URL-encode the # so
  // the data: URL stays valid for any rgb()/rgba()/hex value.
  const encodedColor = encodeURIComponent(color);
  return (
    <label className="block w-full" htmlFor={id}>
      {label && <span className="mb-1.5 block text-sm font-medium text-tg-text">{label}</span>}
      <select
        ref={(node) => {
          probeRef(node);
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        id={id}
        className={cn(
          'w-full appearance-none rounded-input border bg-[var(--color-input-bg)] bg-[length:1rem_1rem] bg-[right_0.75rem_center] bg-no-repeat px-3 py-3 pr-9 text-base text-tg-text shadow-sm outline-none transition focus:border-brand-green focus:bg-tg-bg-secondary focus:ring-2 focus:ring-brand-green/20',
          error ? 'border-destructive' : 'border-[var(--color-input-border)]',
          className,
        )}
        style={{
          backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'><path d='M1 1.5L6 6.5L11 1.5' stroke='${encodedColor}' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>")`,
        }}
        {...props}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error && <span className="mt-1 block text-xs text-destructive">{error}</span>}
    </label>
  );
});
